'use client'
import { type FC, useCallback, useRef } from 'react'
import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
  useMediaQuery,
  VStack
} from '@chakra-ui/react'
import { BiUser } from 'react-icons/bi'
import { useRouter } from 'next/navigation'
import { useUser } from 'auth'
import { CustomerType } from 'ecosystem'
import { fetchJson } from 'api'
import { GrandLink, GrandText } from 'ui'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import StorefrontVatToggle from '../../common/components/StorefrontVatToggle'

const HOVER_CLOSE_DELAY_MS = 150

const DEFAULT_PATHS = {
  login: '/login',
  signup: '/signup',
  account: '/account',
  profile: '/account?menuId=profile',
  logoutApi: '/api/auth/logout'
}

export interface AccountNavItemConfig {
  paths?: Partial<typeof DEFAULT_PATHS>
  vatToggle?: {
    enabled?: boolean
    labelIncludedVat?: string
    labelExcludedVat?: string
  }
}

export interface AccountNavItemProps {
  config?: AccountNavItemConfig
}

export interface AccountNavItemOverrides {
  accountSignInLabel?: string
  accountMyAccountFallback?: string
  accountMyPages?: string
  accountProfile?: string
  accountLogout?: string
  accountNewHere?: string
  accountCreateAccount?: string
  accountViewPricesHeading?: string
  accountBusinessBadge?: string
}

const getInitials = (first?: string, last?: string): string => {
  const f = first?.trim()?.[0] ?? ''
  const l = last?.trim()?.[0] ?? ''
  return (f + l).toUpperCase() || '?'
}

const AccountNavItem: FC<AccountNavItemProps> = ({ config }) => {
  const paths = { ...DEFAULT_PATHS, ...config?.paths }
  const vatToggleConfig = config?.vatToggle
  const vatToggleEnabled = !!vatToggleConfig?.enabled

  const { user, mutateUser } = useUser()
  const router = useRouter()
  const overrides = useOverridesContext<keyof AccountNavItemOverrides>()
  const customer = user?.customer
  const isLoggedIn = !!customer
  const isBusiness = customer?.type === CustomerType.BUSINESS
  const companyName = customer?.companyProfile?.legalName ?? customer?.organizationName ?? null

  // `pointer: fine` excludes coarse-pointer devices (touch). On those we keep
  // the default click-to-open behavior; mice/trackpads get hover-to-open.
  const [canHover] = useMediaQuery('(hover: hover) and (pointer: fine)')
  const { isOpen, onOpen, onClose, onToggle } = useDisclosure()
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleHoverEnter = useCallback(() => {
    if (!canHover) return
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
    onOpen()
  }, [canHover, onOpen])

  const handleHoverLeave = useCallback(() => {
    if (!canHover) return
    // Small delay so the user can travel from button → list across the gap
    // without the menu snapping shut.
    closeTimer.current = setTimeout(onClose, HOVER_CLOSE_DELAY_MS)
  }, [canHover, onClose])

  const handleLogout = useCallback(async () => {
    try {
      await fetchJson(paths.logoutApi, { method: 'POST' })
    } catch {
      // ignore — clearing client state is what matters for UX
    }
    await mutateUser({ customer: null }, false)
    router.refresh()
    router.push(paths.login)
  }, [mutateUser, paths.login, paths.logoutApi, router])

  const signInLabel = overrideText('Logga in', overrides?.accountSignInLabel)
  const myAccountFallback = overrideText('Mitt konto', overrides?.accountMyAccountFallback)
  const myPagesLabel = overrideText('Mina beställningar', overrides?.accountMyPages)
  const profileLabel = overrideText('Profil', overrides?.accountProfile)
  const logoutLabel = overrideText('Logga ut', overrides?.accountLogout)
  const newHereLabel = overrideText('Ny här?', overrides?.accountNewHere)
  const createAccountLabel = overrideText('Skapa konto', overrides?.accountCreateAccount)
  const viewPricesHeading = overrideText('Visa priser', overrides?.accountViewPricesHeading)
  const businessBadgeLabel = overrideText('Företagskonto', overrides?.accountBusinessBadge)

  const initials = getInitials(customer?.firstName, customer?.lastName)
  const displayName =
    [customer?.firstName, customer?.lastName].filter(Boolean).join(' ') ||
    customer?.email ||
    myAccountFallback

  return (
    <Menu
      placement="bottom-end"
      closeOnSelect={false}
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
    >
      <MenuButton
        aria-label={isLoggedIn ? displayName : myAccountFallback}
        _hover={{ opacity: 0.9 }}
        onClick={
          canHover
            ? () => {
                router.push(isLoggedIn ? paths.account : paths.login)
              }
            : onToggle
        }
        onMouseEnter={handleHoverEnter}
        onMouseLeave={handleHoverLeave}
      >
        <Center
          borderRadius="xl"
          color="var(--chakra-colors-component-TopNavContent-AccountNavItem-color, var(--chakra-colors-text-default))"
          fontSize="xl"
          fontWeight="bold"
          h={9}
          letterSpacing="0.5px"
          lineHeight="1"
          w={9}
          position="relative"
        >
          <BiUser />
          {isLoggedIn ? (
            <Flex
              align="center"
              borderRadius="50%"
              fontSize="2xs"
              h={5}
              justify="center"
              position="absolute"
              right="-6px"
              top="-6px"
              w={5}
              zIndex={101}
            >
              <GrandText textAlign="center">{initials}</GrandText>
            </Flex>
          ) : null}
        </Center>
      </MenuButton>
      <MenuList
        cursor="default"
        minW="260px"
        zIndex={9001}
        onMouseEnter={handleHoverEnter}
        onMouseLeave={handleHoverLeave}
        fontSize="sm"
      >
        {isLoggedIn ? (
          <>
            <Box px={3} py={2}>
              <VStack align="stretch" gap={1}>
                <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                  {displayName}
                </Text>
                {customer?.email ? (
                  <Text fontSize="xs" color="text.mild" noOfLines={1}>
                    {customer.email}
                  </Text>
                ) : null}
                {isBusiness && companyName ? (
                  <Text fontSize="xs" fontWeight="medium" noOfLines={1}>
                    {companyName}
                  </Text>
                ) : null}
                {isBusiness ? (
                  <Badge
                    alignSelf="flex-start"
                    colorScheme="purple"
                    fontSize="2xs"
                    mt={1}
                    textTransform="uppercase"
                  >
                    {businessBadgeLabel}
                  </Badge>
                ) : null}
              </VStack>
            </Box>
            <MenuDivider />
            <MenuItem closeOnSelect as="a" href={paths.account}>
              {myPagesLabel}
            </MenuItem>
            <MenuItem closeOnSelect as="a" href={paths.profile}>
              {profileLabel}
            </MenuItem>
          </>
        ) : (
          <Box px={3} py={3}>
            <VStack align="stretch" gap={3}>
              <Button aria-label={signInLabel} as={GrandLink} href={paths.login} w="full">
                {signInLabel}
              </Button>

              <Text fontSize="sm" textAlign="center">
                {newHereLabel}{' '}
                <GrandLink href={paths.signup} color="primary" fontWeight="semibold">
                  {createAccountLabel}
                </GrandLink>
              </Text>
            </VStack>
          </Box>
        )}

        {vatToggleEnabled ? (
          <>
            <MenuDivider />
            <Box px={3} py={3}>
              <VStack align="stretch" gap={2}>
                <Text color="text.mild" fontSize="xs" textTransform="uppercase">
                  {viewPricesHeading}
                </Text>
                <StorefrontVatToggle
                  labelExcludedVat={vatToggleConfig?.labelExcludedVat}
                  labelIncludedVat={vatToggleConfig?.labelIncludedVat}
                />
              </VStack>
            </Box>
          </>
        ) : null}

        {isLoggedIn ? (
          <>
            <MenuDivider />
            <MenuItem
              closeOnSelect
              color="status.error"
              onClick={() => {
                void handleLogout()
              }}
            >
              {logoutLabel}
            </MenuItem>
          </>
        ) : null}
      </MenuList>
    </Menu>
  )
}

export default AccountNavItem
